import { Fragment } from 'react';

import { Trans } from '@lingui/react/macro';
import { Typography } from '@mui/material';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';

import { detailDashParamsAtom, filtersAtom, paramsAtom } from './atoms';
import { useTableauViz } from './hooks';
import type { Dashboard } from './types';
import { StyledAlert, TableauDashboardContainer } from './VideaInsights.styled';
import { VideaLoader } from '@/common-components/VideaLoader';

/**
 * There is a known issue with the Embedding API v3 where the viz-filter doesn’t
 * handle a value of empty string, signifying "All", correctly. Instead, the
 * default value is applied.
 * https://help.tableau.com/current/api/embedding_api/en-us/docs/embedding_api_known_issues.html#viz-filter-doesnt-handle-all-correctly
 */
const getFilterValue = (appliedValues: string[], isAllSelected: boolean) => {
  if (isAllSelected) return '';
  if (appliedValues.length) return appliedValues.join(',');
  return 'None';
};

interface Props {
  dashboard: Dashboard;
  isErrorToken: boolean;
  refetchToken: () => void;
  token?: string;
  url: string;
}

export const TableauDashboard = ({
  dashboard,
  isErrorToken,
  refetchToken,
  token,
  url,
}: Props) => {
  const parameters = useAtomValue(paramsAtom);
  const filters = useAtomValue(filtersAtom);
  const detailDashParams = useAtomValue(detailDashParamsAtom);

  const { isLoadingViz, isVizLoadError } = useTableauViz(
    token,
    refetchToken,
    dashboard,
  );

  const showError = isErrorToken || isVizLoadError;
  const showLoading = (isLoadingViz || !token) && !showError;

  const isDetailDashboard =
    dashboard === 'PROVIDER_DETAIL' || dashboard === 'PRACTICE_DETAIL';

  const parametersToRender = isDetailDashboard ? detailDashParams : parameters;

  return (
    <TableauDashboardContainer>
      {showError && (
        <StyledAlert severity="error">
          <Typography className="error-text">
            <Trans>There was a problem loading the Tableau dashboard</Trans>
          </Typography>
        </StyledAlert>
      )}
      {showLoading && (
        <VideaLoader
          style={{
            top: '20vh',
            position: 'absolute',
            margin: '0 auto',
            left: 0,
            right: 0,
          }}
        />
      )}
      {token && (
        <div
          className={classNames({ 'visibility-hidden': isLoadingViz })}
          id="tableau-viz-container"
        >
          {/* @ts-expect-error */}
          <tableau-viz
            device="desktop"
            id={`tableau-viz-${dashboard}`}
            src={url}
            token={token}
            toolbar="hidden"
          >
            {Object.entries(parametersToRender || {}).map(([name, value]) => (
              <Fragment key={name}>
                {/* @ts-expect-error */}
                <viz-filter field={name} value={value}></viz-filter>
              </Fragment>
            ))}
            {Object.entries(filters).map(
              ([name, { appliedValues, isAllSelected }]) => (
                <Fragment key={name}>
                  {/* @ts-expect-error */}
                  <viz-filter
                    field={name}
                    // TODO: Fix comma issue - not filtering correctly for values containing commas
                    value={getFilterValue(appliedValues, isAllSelected)}
                    // @ts-expect-error
                  ></viz-filter>
                </Fragment>
              ),
            )}
            {/* @ts-expect-error */}
          </tableau-viz>
        </div>
      )}
    </TableauDashboardContainer>
  );
};
